import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Action, Banner, ErrorNote, Field, FieldGrid, Input, Loading, Panel, PanelHead, SaveBar, SettingRow, Toggle } from '../../components/aa';
import { pricingApi, type E2bPriceCheck, type PricingSettings, type PricingUpdate } from '../../services/pricingApi';

type Draft = Record<Exclude<keyof PricingUpdate, 'bill_aibot_usage'>, string> & { bill_aibot_usage: boolean };

type NumberKey = Exclude<keyof PricingUpdate, 'bill_aibot_usage'>;

const DOLLAR_KEYS: Array<NumberKey> = [
  'seat_price_cents', 'seat_allowance_cents', 'topup_threshold_cents',
  'topup_amount_cents', 'free_starter_credit_cents', 'deep_scoping_price_cents',
];
const PERCENT_KEYS: Array<NumberKey> = ['platform_margin_percent', 'compute_margin_percent'];
const RATE_KEYS: Array<NumberKey> = ['compute_vcpu_hour_usd', 'compute_gib_hour_usd'];

const toDraft = (s: PricingSettings): Draft => {
  const draft = { bill_aibot_usage: s.bill_aibot_usage } as Draft;
  DOLLAR_KEYS.forEach((k) => { draft[k] = ((s[k] as number) / 100).toFixed(2); });
  [...PERCENT_KEYS, ...RATE_KEYS].forEach((k) => { draft[k] = s[k] == null ? '' : String(s[k]); });
  return draft;
};

const day = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }) : 'never');

const E2bNotice: React.FC<{ check: E2bPriceCheck | null; saved: Draft; busy: boolean; onApply: () => void }> = ({ check, saved, busy, onApply }) => {
  if (check?.failing_since) {
    return (
      <Banner className="mb-4" title="E2B's prices could not be read"
        desc={`The daily check has failed since ${day(check.failing_since)}. Your rates are unchanged; compare them with e2b.dev/pricing by hand.`} />
    );
  }
  if (!check?.differs || check.vcpu_hour_usd == null || check.gib_hour_usd == null) return null;
  const plan = check.plan_monthly_usd == null ? '' : ` Their plan fee is $${check.plan_monthly_usd} a month.`;
  return (
    <Banner className="mb-4" title="E2B's published prices are different from yours"
      desc={`E2B lists $${check.vcpu_hour_usd} per vCPU-hour and $${check.gib_hour_usd} per GiB-hour; you have $${saved.compute_vcpu_hour_usd} and $${saved.compute_gib_hour_usd}.${plan} Nothing changes until you apply them.`}
      action={<Action onClick={onApply} disabled={busy} reason="Saving">Use E2B's prices</Action>} />
  );
};

/* Only the fields that changed are sent, so a margin left alone is never
 * rewritten and a builder outage cannot block a seat price change. */
const changesOf = (draft: Draft, saved: Draft): PricingUpdate | string => {
  const update: PricingUpdate = {};
  for (const k of DOLLAR_KEYS) {
    if (draft[k] === saved[k]) continue;
    if (!draft[k].trim()) return 'Enter a price';
    const dollars = Number(draft[k]);
    if (!Number.isFinite(dollars) || dollars < 0) return 'Prices must be zero or more.';
    (update as Record<string, number>)[k] = Math.round(dollars * 100);
  }
  for (const k of PERCENT_KEYS) {
    if (draft[k] === saved[k]) continue;
    if (!draft[k].trim()) return 'Enter a margin';
    const pct = Number(draft[k]);
    if (!Number.isFinite(pct) || pct < 0 || pct > 500) return 'Margins must be between 0 and 500 percent.';
    (update as Record<string, number>)[k] = pct;
  }
  for (const k of RATE_KEYS) {
    if (draft[k] === saved[k]) continue;
    const rate = Number(draft[k]);
    if (!draft[k].trim() || !Number.isFinite(rate) || rate <= 0 || rate >= 3600) return 'Box rates must be more than zero.';
    (update as Record<string, number>)[k] = rate;
  }
  if (draft.bill_aibot_usage !== saved.bill_aibot_usage) update.bill_aibot_usage = draft.bill_aibot_usage;
  return update;
};

const Money: React.FC<{ id: NumberKey; label: string; hint: string; draft: Draft; set: (d: Draft) => void }> = ({
  id, label, hint, draft, set,
}) => (
  <Field label={label} htmlFor={id} hint={hint}>
    <Input id={id} inputMode="decimal" value={draft[id]} onChange={(e) => set({ ...draft, [id]: e.target.value })} />
  </Field>
);

const PricingSettingsPage: React.FC = () => {
  const [saved, setSaved] = useState<Draft | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [builderUp, setBuilderUp] = useState(true);
  const [listPrice, setListPrice] = useState<Pick<PricingSettings, 'e2b_vcpu_hour_usd' | 'e2b_gib_hour_usd' | 'e2b_price_check'> | null>(null);
  const [failed, setFailed] = useState(false);
  const [busy, setBusy] = useState(false);

  const apply = (data: PricingSettings) => {
    const next = toDraft(data);
    setSaved(next);
    setDraft(next);
    setBuilderUp(data.builder_available);
    setListPrice({ e2b_vcpu_hour_usd: data.e2b_vcpu_hour_usd, e2b_gib_hour_usd: data.e2b_gib_hour_usd, e2b_price_check: data.e2b_price_check });
  };

  useEffect(() => {
    pricingApi.getPricing().then(({ data }) => apply(data)).catch(() => setFailed(true));
  }, []);

  if (failed) return <ErrorNote message="Could not load pricing. Refresh to try again." />;
  if (!draft || !saved) return <Loading label="Loading pricing" />;

  const changes = changesOf(draft, saved);
  const dirty = typeof changes === 'string' || Object.keys(changes).length > 0;
  const check = listPrice?.e2b_price_check ?? null;
  const listHint = (published: number | null | undefined, fallback: number | null | undefined) => {
    if (published != null) return `E2B price: $${published} (read ${day(check?.read_at ?? null)}).`;
    if (fallback != null) return `E2B list price: $${fallback}.`;
    return undefined;
  };

  const runE2b = async (work: () => Promise<{ data: PricingSettings }>, done: string, failed: string) => {
    setBusy(true);
    try {
      const { data } = await work();
      apply(data);
      toast.success(done);
    } catch {
      toast.error(failed);
    } finally {
      setBusy(false);
    }
  };
  const applyE2b = () => runE2b(
    () => pricingApi.updatePricing({ compute_vcpu_hour_usd: check?.vcpu_hour_usd ?? undefined, compute_gib_hour_usd: check?.gib_hour_usd ?? undefined }),
    'Box rates updated to E2B prices.',
    'Could not update the box rates.',
  );
  const checkE2b = () => runE2b(() => pricingApi.checkE2bPrices(), 'E2B prices checked.', 'Could not check E2B prices.');

  const save = async () => {
    if (typeof changes === 'string') {
      toast.error(changes);
      return;
    }
    setBusy(true);
    try {
      const { data } = await pricingApi.updatePricing(changes);
      apply(data);
      toast.success('Pricing saved.');
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not save pricing.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[1000px] space-y-6">
      <Panel>
        <PanelHead
          kicker="Seats"
          title="Platform seat"
          desc="Charged per person, monthly, in advance. A change applies from each company's next billing period, never mid-period."
        />
        <div className="px-6 pb-6">
          <FieldGrid>
            <Money id="seat_price_cents" label="Seat price (USD per month)" hint="Per person on the company." draft={draft} set={setDraft} />
            <Money id="seat_allowance_cents" label="Included usage per seat (USD per month)" hint="Pooled across the company, resets monthly." draft={draft} set={setDraft} />
            <Money id="free_starter_credit_cents" label="Free plan starter credit (USD, one time)" hint="Given once to a new company, no card needed." draft={draft} set={setDraft} />
          </FieldGrid>
        </div>
      </Panel>

      <Panel>
        <PanelHead
          kicker="Usage"
          title="Margins"
          desc="Every model call and box hour is metered at true cost. The margin sets what a company pays. Changes apply to usage from now on; past usage keeps the rate it was charged at."
        />
        <div className="px-6 pb-6">
          {!builderUp && (
            <Banner className="mb-4" title="The builder is not reachable" desc="Margins live on the builder. They can be edited once it answers again." />
          )}
          <FieldGrid>
            <Field label="AI usage margin (%)" htmlFor="platform_margin_percent" hint="0 sells at cost. Sets every model's rate unless that model has its own price.">
              <Input id="platform_margin_percent" inputMode="decimal" disabled={!builderUp} value={draft.platform_margin_percent}
                onChange={(e) => setDraft({ ...draft, platform_margin_percent: e.target.value })} />
            </Field>
            <Field label="Compute margin (%)" htmlFor="compute_margin_percent" hint="0 passes workspace box time through at cost.">
              <Input id="compute_margin_percent" inputMode="decimal" disabled={!builderUp} value={draft.compute_margin_percent}
                onChange={(e) => setDraft({ ...draft, compute_margin_percent: e.target.value })} />
            </Field>
          </FieldGrid>
        </div>
        <SettingRow
          title="Bill Orbit and chat AI usage"
          desc="When on, AI used by Orbit, intake and chat counts against each company's included usage and balance, at the AI usage margin. Off: it is our cost."
          control={<Toggle checked={draft.bill_aibot_usage} onChange={(next) => setDraft({ ...draft, bill_aibot_usage: next })} label="Bill Orbit and chat AI usage" />}
        />
      </Panel>

      <Panel>
        <PanelHead
          kicker="Usage"
          title="Box rates"
          desc="What one hour of a workspace box costs us. Box time is metered per second at these rates, then the compute margin is added. Changes apply to box time from now on; time already billed keeps its rate."
        />
        <div className="px-6 pb-6">
          <E2bNotice check={check} saved={saved} busy={busy} onApply={applyE2b} />
          <FieldGrid>
            <Field label="vCPU rate (USD per vCPU per hour)" htmlFor="compute_vcpu_hour_usd" hint={listHint(check?.vcpu_hour_usd, listPrice?.e2b_vcpu_hour_usd)}>
              <Input id="compute_vcpu_hour_usd" inputMode="decimal" disabled={!builderUp} value={draft.compute_vcpu_hour_usd}
                onChange={(e) => setDraft({ ...draft, compute_vcpu_hour_usd: e.target.value })} />
            </Field>
            <Field label="Memory rate (USD per GiB per hour)" htmlFor="compute_gib_hour_usd" hint={listHint(check?.gib_hour_usd, listPrice?.e2b_gib_hour_usd)}>
              <Input id="compute_gib_hour_usd" inputMode="decimal" disabled={!builderUp} value={draft.compute_gib_hour_usd}
                onChange={(e) => setDraft({ ...draft, compute_gib_hour_usd: e.target.value })} />
            </Field>
          </FieldGrid>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-[12px] leading-[20px] text-ink-dim">
            <span>E2B prices are checked daily. Last check: {day(check?.checked_at ?? null)}.</span>
            <Action variant="outline" className="min-w-[120px]" onClick={checkE2b} disabled={busy || !builderUp} reason={builderUp ? 'Saving' : 'The builder is not reachable'}>Check now</Action>
          </div>
        </div>
      </Panel>

      {/* Each Owner sets their own automatic top-up on Billing; these stay the default for new companies.
      <Panel>
        <PanelHead kicker="Wallet" title="Prepaid balance and top-up" desc="Usage past the allowance comes out of a prepaid balance. Work pauses at zero." />
        <div className="px-6 pb-6">
          <FieldGrid>
            <Money id="topup_threshold_cents" label="Top up when balance falls below (USD)" hint="Checked after each charge." draft={draft} set={setDraft} />
            <Money id="topup_amount_cents" label="Top-up amount (USD)" hint="Charged to the saved card. Must be more than the threshold." draft={draft} set={setDraft} />
          </FieldGrid>
        </div>
      </Panel>
      */}

      {/* Deep scoping is free while its paywall is off (ORBIT_DEEP_SCOPING_PAYWALL), so its price is hidden.
      <Panel>
        <PanelHead kicker="One-time" title="Deep scoping" desc="The one-time price of guided deep scoping." />
        <div className="px-6 pb-6">
          <FieldGrid>
            <Money id="deep_scoping_price_cents" label="Deep scoping price (USD)" hint="Charged to the company's billing account." draft={draft} set={setDraft} />
          </FieldGrid>
        </div>
      </Panel>
      */}

      <SaveBar onSave={save} onRevert={() => setDraft(saved)} saveLabel="Save pricing" dirty={dirty} busy={busy} />
    </div>
  );
};

export default PricingSettingsPage;
